import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { Menu, X, Globe, Phone, User as UserIcon, ClipboardList, LogOut, ChevronDown, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import BrandLogo from "./BrandLogo";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { pathname } = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/packages", label: t("nav.packages") },
    { to: "/haji-khusus", label: "Haji Khusus" },
    { to: "/blog", label: t("nav.blog") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-border shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20 lg:h-24">
        <Link to="/" className="flex items-center" data-testid="logo-link" aria-label="BATIK Tour & Travel — PT Baitullah Tiga Kharisma">
          <BrandLogo layout="horizontal" className="h-14 lg:h-16 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            l.to === "/haji-khusus" ? (
              <DropdownMenu key={l.to}>
                <DropdownMenuTrigger
                  data-testid="nav-haji-khusus-trigger"
                  className="text-sm tracking-wide link-underline text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
                >
                  {l.label}
                  <ChevronDown className="h-3 w-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[220px]">
                  <DropdownMenuItem asChild data-testid="haji-menu-program">
                    <Link to="/haji-khusus" className="w-full cursor-pointer">Program Haji Khusus</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="haji-menu-cek-porsi">
                    <a
                      href="https://haji.go.id/estimasi-keberangkatan"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full cursor-pointer inline-flex items-center gap-2"
                    >
                      Cek Porsi Haji
                      <ExternalLink className="h-3 w-3 opacity-60" />
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.to.replace("/", "") || "home"}-link`}
                className={({ isActive }) =>
                  `text-sm tracking-wide link-underline transition-colors ${
                    isActive ? "text-primary font-semibold" : "text-muted-foreground hover:text-primary"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-switcher"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-accent transition-colors px-3 py-2 rounded-full border border-border bg-white/40"
            >
              <Globe className="h-4 w-4" />
              <span className="font-medium uppercase tracking-wider text-xs">{lang}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[120px]">
              <DropdownMenuItem
                data-testid="lang-id"
                onClick={() => setLang("id")}
                className={lang === "id" ? "font-semibold text-primary" : ""}
              >
                🇮🇩 Bahasa
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="lang-en"
                onClick={() => setLang("en")}
                className={lang === "en" ? "font-semibold text-primary" : ""}
              >
                🇬🇧 English
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/packages" className="hidden sm:inline-flex btn-accent !py-2.5 !px-5 !text-xs" data-testid="header-book-cta">
            {t("nav.book")}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="user-menu-trigger"
                className="hidden md:inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border border-border bg-white/40 hover:bg-white/70 transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                ) : (
                  <span className="h-7 w-7 rounded-full bg-primary text-white text-xs font-medium flex items-center justify-center">
                    {(user.name || "?").charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="text-xs text-primary font-medium max-w-[100px] truncate">{user.name?.split(" ")[0] || "Akun"}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                {user.is_admin ? (
                  <DropdownMenuItem asChild data-testid="menu-admin">
                    <Link to="/admin" className="cursor-pointer"><UserIcon className="h-4 w-4 mr-2" /> Admin Dashboard</Link>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem asChild data-testid="menu-my-bookings">
                    <Link to="/my-bookings" className="cursor-pointer"><ClipboardList className="h-4 w-4 mr-2" /> Booking Saya</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="cursor-pointer text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              className="hidden md:inline-flex items-center gap-1.5 text-sm text-primary hover:text-accent px-3 py-2 rounded-full border border-border bg-white/40 transition-colors"
              data-testid="header-login-link"
            >
              <UserIcon className="h-4 w-4" />
              <span className="text-xs font-medium">Masuk</span>
            </Link>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="lg:hidden p-2 rounded-full hover:bg-muted transition-colors"
                data-testid="mobile-menu-trigger"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5 text-primary" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-background border-l border-border p-0">
              <VisuallyHidden.Root>
                <SheetTitle>Navigation</SheetTitle>
                <SheetDescription>Site navigation menu</SheetDescription>
              </VisuallyHidden.Root>
              <div className="flex flex-col h-full">
                <div className="p-6 border-b border-border flex items-center justify-between">
                  <BrandLogo layout="horizontal" className="h-16 w-auto" />
                  <button onClick={() => setOpen(false)} aria-label="Close" data-testid="mobile-menu-close">
                    <X className="h-5 w-5 text-primary" />
                  </button>
                </div>
                <nav className="flex flex-col p-6 gap-1">
                  {links.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      end={l.to === "/"}
                      data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                      className={({ isActive }) =>
                        `py-3 px-3 rounded-md font-serif text-lg transition-colors ${
                          isActive ? "bg-muted text-primary" : "text-muted-foreground hover:bg-muted hover:text-primary"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="mt-auto p-6 border-t border-border space-y-3">
                  <a
                    href="https://wa.me/6282114294101"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full justify-center"
                    data-testid="mobile-whatsapp-cta"
                  >
                    <Phone className="h-4 w-4" /> WhatsApp
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
